import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, CircularProgress, LinearProgress } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getCampaignDetail } from '../../api/incentiveApi';
import { getAllSales } from '../../api/salesApi';
import { getAllAgents } from '../../api/userApi';
import { getSlipsByCampaign } from '../../api/incentiveSlipApi';
import dayjs from 'dayjs';

const PP = "'Poppins', sans-serif";
const formatRM = (v) => `RM ${(v || 0).toLocaleString('en-MY', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const API_BASE = process.env.REACT_APP_API_BASE_URL?.replace(/\/api$/, '') || '';

const getInitials = (name) => {
    if (!name) return '??';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase();
};

const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
    return match ? `https://www.youtube.com/embed/${match[1]}` : null;
};

// Tier colors for progress bar markers
const TIER_COLORS = ['#F59E0B', '#10B981', '#1A8CFF', '#8B5CF6', '#EF4444'];

export default function CampaignDetailPage() {
    const { campaignId } = useParams();
    const navigate = useNavigate();
    const [campaign, setCampaign] = useState(null);
    const [tiers, setTiers] = useState([]);
    const [agentProgress, setAgentProgress] = useState([]);
    const [slips, setSlips] = useState([]);
    const [loading, setLoading] = useState(true);


    const fetchData = useCallback(async () => {
        try {
            const campRes = await getCampaignDetail(campaignId);
            const camp = campRes.data;
            setCampaign(camp);
            setTiers(camp.tiers || []);

            const [salesRes, agentsRes, slipsRes] = await Promise.all([
                getAllSales(),
                getAllAgents(),
                // Everyone's payouts — gated server-side to slip generators / incentive
                // managers. An ordinary agent gets 403 here and simply sees no slip table;
                // without the catch the whole page would fail to load for them.
                getSlipsByCampaign(campaignId).catch(() => ({ data: [] })),
            ]);

            setSlips(slipsRes.data || []);

            const sales = salesRes.data || [];
            const agents = agentsRes.data || [];
            const campStart = new Date(camp.startDate);
            const campEnd = new Date(camp.endDate);
            const sortedTiers = [...(camp.tiers || [])].sort((a, b) => b.target - a.target);

            const campaignSales = sales.filter(s => {
                const d = new Date(s.depositOn);
                return d >= campStart && d <= campEnd;
            });

            // Group sales by agent, handling co-broke /2
            const agentTotals = {};
            campaignSales.forEach(s => {
                const isCoBroke = s.coAgentId && s.coAgentId > 0;
                const rev = (s.finalPrice || 0) + (s.addOnPrice || 0);
                const kw = s.energy || 0;

                // Main agent
                if (!agentTotals[s.agentId]) agentTotals[s.agentId] = { revenue: 0, kwp: 0, count: 0 };
                agentTotals[s.agentId].revenue += isCoBroke ? rev / 2 : rev;
                agentTotals[s.agentId].kwp += isCoBroke ? kw / 2 : kw;
                agentTotals[s.agentId].count += isCoBroke ? 0.5 : 1;

                // Co-agent
                if (isCoBroke) {
                    if (!agentTotals[s.coAgentId]) agentTotals[s.coAgentId] = { revenue: 0, kwp: 0, count: 0 };
                    agentTotals[s.coAgentId].revenue += rev / 2;
                    agentTotals[s.coAgentId].kwp += kw / 2;
                    agentTotals[s.coAgentId].count += 0.5;
                }
            });

            const progress = Object.entries(agentTotals).map(([agentId, totals]) => {
                const agent = agents.find(a => String(a.userId) === String(agentId));
                const { revenue, kwp, count } = totals;
                const metricValue = camp.metric === 'Revenue' ? revenue : camp.metric === 'kWp' ? kwp : count;
                const tier = sortedTiers.find(t => metricValue >= t.target);
                const agentName = agent
                    ? (`${agent.lastName || ''} ${agent.firstName || ''}`.trim() || agent.alias || `Agent #${agentId}`)
                    : `Agent #${agentId}`;

                return {
                    agentId: parseInt(agentId),
                    agentName,
                    profilePhotoUrl: agent?.profilePhotoUrl,
                    revenue, kwp, count, metricValue,
                    tierName: tier?.tierName || '—',
                    reward: tier?.reward || '—',
                    qualified: !!tier,
                };
            }).sort((a, b) => b.metricValue - a.metricValue);

            setAgentProgress(progress);
        } catch (err) {
            console.error('Error loading campaign:', err);
        } finally {
            setLoading(false);
        }
    }, [campaignId]);

    useEffect(() => { fetchData(); }, [fetchData]);

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
    if (!campaign) return <Typography>Campaign not found</Typography>;

    const isEnded = new Date(campaign.endDate) < new Date();
    const hasSlips = slips.length > 0;
    const sortedTiers = [...tiers].sort((a, b) => a.sortOrder - b.sortOrder);
    const maxTarget = sortedTiers.length ? Math.max(...sortedTiers.map(t => t.target)) : 0;
    const embedUrl = getYouTubeEmbedUrl(campaign.youTubeUrl);

    const headSx = { fontSize: '0.72rem', fontWeight: 600, color: '#64748B', fontFamily: PP, backgroundColor: '#F8FAFC', borderBottom: '2px solid #E2E8F0', padding: '8px 12px' };
    const cellSx = { fontSize: '0.8rem', fontFamily: PP, padding: '8px 12px', borderBottom: '1px solid #F1F5F9' };

    // Progress bar component for each agent
    const ProgressBar = ({ metricValue }) => {
        const pct = maxTarget > 0 ? Math.min(100, (metricValue / maxTarget) * 100) : 0;
        return (
            <Box sx={{ position: 'relative', width: '100%', minWidth: 200 }}>
                <LinearProgress
                    variant="determinate"
                    value={pct}
                    sx={{ height: 14, borderRadius: 7, backgroundColor: '#F1F5F9', '& .MuiLinearProgress-bar': { borderRadius: 7, background: 'linear-gradient(90deg, #1A8CFF, #10B981)' } }}
                />
                {/* Tier markers */}
                {sortedTiers.map((t, i) => {
                    const pos = maxTarget > 0 ? (t.target / maxTarget) * 100 : 0;
                    return (
                        <Box key={t.tierId} sx={{ position: 'absolute', top: -2, left: `${pos}%`, transform: 'translateX(-50%)' }}>
                            <Box sx={{
                                width: 6, height: 18, borderRadius: 3,
                                backgroundColor: metricValue >= t.target ? TIER_COLORS[i % TIER_COLORS.length] : '#CBD5E1',
                                border: '1px solid white',
                            }} />
                            <Typography sx={{ fontSize: '0.55rem', color: '#64748B', fontFamily: PP, textAlign: 'center', mt: 0.2, whiteSpace: 'nowrap' }}>
                                {t.tierName}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
        );
    };

    return (
        <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <IconButton onClick={() => navigate(-1)}><ArrowBackIcon /></IconButton>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, fontFamily: PP }}>{campaign.name}</Typography>
                    <Typography sx={{ fontSize: '0.8rem', color: '#64748B', fontFamily: PP }}>
                        {dayjs(campaign.startDate).format('DD MMM YYYY')} — {dayjs(campaign.endDate).format('DD MMM YYYY')}
                        &nbsp;·&nbsp;Metric: {campaign.metric}
                        {isEnded && <Chip label="Ended" size="small" sx={{ ml: 1, fontSize: '0.6rem', bgcolor: '#F1F5F9', color: '#64748B' }} />}
                    </Typography>
                </Box>
                <Chip label={campaign.status} sx={{
                    fontWeight: 600, fontSize: '0.72rem', fontFamily: PP,
                    backgroundColor: campaign.status === 'Active' ? '#D1FAE5' : campaign.status === 'Draft' ? '#F1F5F9' : '#FEF3C7',
                    color: campaign.status === 'Active' ? '#065F46' : campaign.status === 'Draft' ? '#64748B' : '#92400E',
                }} />
                {/* No "Generate slips" here any more: incentive slips come only from the Slips
                    page (Incentive tab), for claims whose money is recorded as received. This
                    page's generator counted straight from sales and is retired. (user 2026-09-21) */}
            </Box>

            {/* Image + Video (kept at top) */}
            {(campaign.imageUrl || embedUrl) && (
                <Box sx={{ mb: 3, p: 2.5, backgroundColor: '#F8FAFC', borderRadius: '12px', border: '1px solid #E2E8F0' }}>
                    {campaign.imageUrl && (
                        <Box sx={{ mb: embedUrl ? 2 : 0 }}>
                            <img src={`${API_BASE}${campaign.imageUrl}`} alt={campaign.name}
                                style={{ maxWidth: '100%', maxHeight: 300, borderRadius: 10, objectFit: 'cover' }} />
                        </Box>
                    )}
                    {embedUrl && (
                        <Box sx={{ position: 'relative', paddingBottom: '56.25%', height: 0, borderRadius: '10px', overflow: 'hidden' }}>
                            <iframe src={embedUrl} title="YouTube" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }} allowFullScreen />
                        </Box>
                    )}
                </Box>
            )}

            {/* Tiers */}
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', fontFamily: PP, mb: 1 }}>Tiers</Typography>
            <TableContainer component={Paper} elevation={0} sx={{ mb: 3, border: '1px solid #E2E8F0', borderRadius: '10px' }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headSx}>Tier</TableCell>
                            <TableCell sx={headSx}>Target ({campaign.metric})</TableCell>
                            <TableCell sx={headSx}>Reward</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedTiers.map((t, i) => (
                            <TableRow key={t.tierId}>
                                <TableCell sx={{ ...cellSx, fontWeight: 600 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: TIER_COLORS[i % TIER_COLORS.length] }} />
                                        {t.tierName}
                                    </Box>
                                </TableCell>
                                <TableCell sx={cellSx}>{campaign.metric === 'Revenue' ? formatRM(t.target) : t.target}</TableCell>
                                <TableCell sx={cellSx}>{t.reward}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Agent Leaderboard */}
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', fontFamily: PP, mb: 1 }}>Leaderboard ({agentProgress.length} agents)</Typography>
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: '10px' }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ ...headSx, width: 40 }}>#</TableCell>
                            <TableCell sx={headSx}>Agent</TableCell>
                            <TableCell sx={{ ...headSx, minWidth: 220 }}>Progress</TableCell>
                            <TableCell sx={{ ...headSx, textAlign: 'right' }}>Total Revenue</TableCell>
                            <TableCell sx={{ ...headSx, textAlign: 'right' }}>kWp</TableCell>
                            <TableCell sx={{ ...headSx, textAlign: 'right' }}>Sales</TableCell>
                            <TableCell sx={headSx}>Tier</TableCell>
                            <TableCell sx={headSx}>Reward</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {agentProgress.map((a, idx) => (
                            <TableRow key={a.agentId} sx={{ backgroundColor: a.qualified ? '#F0FDF4' : 'inherit' }}>
                                <TableCell sx={{ ...cellSx, fontWeight: 700, color: idx < 3 ? '#F59E0B' : '#94A3B8' }}>
                                    {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : idx + 1}
                                </TableCell>
                                <TableCell sx={cellSx}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {a.profilePhotoUrl ? (
                                            <img src={`${API_BASE}${a.profilePhotoUrl}`} alt={a.agentName}
                                                style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover' }} />
                                        ) : (
                                            <Box sx={{
                                                width: 28, height: 28, borderRadius: '50%', backgroundColor: '#E0E7FF',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                fontSize: '0.6rem', fontWeight: 700, color: '#3730A3',
                                            }}>
                                                {getInitials(a.agentName)}
                                            </Box>
                                        )}
                                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, fontFamily: PP }}>{a.agentName}</Typography>
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ ...cellSx, pr: 3 }}>
                                    <ProgressBar metricValue={a.metricValue} />
                                </TableCell>
                                <TableCell sx={{ ...cellSx, textAlign: 'right' }}>{formatRM(a.revenue)}</TableCell>
                                <TableCell sx={{ ...cellSx, textAlign: 'right' }}>{a.kwp.toFixed(2)}</TableCell>
                                <TableCell sx={{ ...cellSx, textAlign: 'right' }}>{a.count}</TableCell>
                                <TableCell sx={cellSx}>
                                    {a.qualified ? <Chip label={a.tierName} size="small" sx={{ fontSize: '0.65rem', bgcolor: '#D1FAE5', color: '#065F46', fontFamily: PP }} /> : '—'}
                                </TableCell>
                                <TableCell sx={cellSx}>{a.reward}</TableCell>
                            </TableRow>
                        ))}
                        {!agentProgress.length && (
                            <TableRow><TableCell colSpan={8} sx={{ ...cellSx, textAlign: 'center', color: '#94A3B8' }}>No sales during campaign period</TableCell></TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Description & Terms */}
            {campaign.description && (
                <Box sx={{ mt: 3, p: 2.5, backgroundColor: '#F8FAFC', borderRadius: '12px', border: '1px solid #E2E8F0' }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '1rem', fontFamily: PP, mb: 1 }}>Description & Terms</Typography>
                    <Typography sx={{ fontSize: '0.85rem', fontFamily: PP, whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
                        {campaign.description}
                    </Typography>
                </Box>
            )}

            {/* Existing Slips */}
            {hasSlips && (
                <Box sx={{ mt: 3 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '1rem', fontFamily: PP, mb: 1 }}>Generated Slips ({slips.length})</Typography>
                    <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: '10px' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={headSx}>Agent</TableCell>
                                    <TableCell sx={headSx}>Tier</TableCell>
                                    <TableCell sx={headSx}>Reward</TableCell>
                                    <TableCell sx={{ ...headSx, textAlign: 'right' }}>Amount</TableCell>
                                    <TableCell sx={headSx}>Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {slips.map(s => (
                                    <TableRow key={s.incentiveSlipId}>
                                        <TableCell sx={{ ...cellSx, fontWeight: 600 }}>{s.agentName}</TableCell>
                                        <TableCell sx={cellSx}>{s.tierAchieved}</TableCell>
                                        <TableCell sx={cellSx}>{s.reward}</TableCell>
                                        <TableCell sx={{ ...cellSx, textAlign: 'right', color: '#10B981', fontWeight: 600 }}>{s.rewardAmount ? formatRM(s.rewardAmount) : '—'}</TableCell>
                                        <TableCell sx={cellSx}>
                                            <Chip label={s.status} size="small" sx={{
                                                fontSize: '0.65rem', fontFamily: PP, fontWeight: 600,
                                                backgroundColor: s.status === 'Approved' ? '#D1FAE5' : s.status === 'Voided' ? '#FEE2E2' : '#FEF3C7',
                                                color: s.status === 'Approved' ? '#065F46' : s.status === 'Voided' ? '#991B1B' : '#92400E',
                                            }} />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}
        </Box>
    );
}
